import { useState } from 'react';
import { Button, CircularProgress, Avatar, Stack, IconButton } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { uploadImage, deleteImage } from './actions/imageUploaderActions';

/**
 * ImageUploader - קומפוננטה סטנד-אלון להעלאת ומחיקת תמונות
 * 
 * שימושים:
 * - רקע דשבורד
 * - ווידג'ט תמונה
 * - לוגו ארגון
 * - רקע מסך
 * 
 * @param {string} currentImage - URL של התמונה הנוכחית
 * @param {function} onImageChange - callback שמקבל את ה-URL החדש או null (במחיקה)
 * @param {string} folder - תיקייה ב-Storage (ברירת מחדל: 'images')
 * @param {number} maxSizeMB - גודל מקסימלי בMB (ברירת מחדל: 5)
 * @param {boolean} showPreview - האם להציג תצוגה מקדימה (ברירת מחדל: true)
 * @param {string} variant - סגנון הכפתור: 'contained' | 'outlined' (ברירת מחדל: 'contained')
 * @param {boolean} fullWidth - האם הכפתור ברוחב מלא
 */
const ImageUploader = ({
    currentImage = '',
    onImageChange,
    folder = 'images',
    maxSizeMB = 5,
    showPreview = true,
    variant = 'contained',
    fullWidth = false
}) => {
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // בדיקת סוג קובץ
        if (!file.type.startsWith('image/')) {
            alert('יש להעלות קובץ תמונה בלבד');
            return;
        }

        // בדיקת גודל
        if (file.size > maxSizeMB * 1024 * 1024) {
            alert(`גודל הקובץ חייב להיות עד ${maxSizeMB}MB`);
            return;
        }

        setUploading(true);
        try {
            const url = await uploadImage(file, folder);
            if (onImageChange) {
                onImageChange(url);
            }
        } catch (err) {
            console.error('Error uploading image:', err);
            alert('שגיאה בהעלאת התמונה');
        } finally {
            setUploading(false);
        }
    };

    const handleImageDelete = async () => {
        if (!currentImage) return;

        setDeleting(true);
        try {
            await deleteImage(currentImage);
            if (onImageChange) {
                onImageChange(null);
            }
        } catch (err) {
            console.error('Error deleting image:', err);
            alert('שגיאה במחיקת התמונה');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Stack spacing={2} alignItems="center">
            {/* תצוגה מקדימה */}
            {showPreview && currentImage && (
                <Avatar
                    src={currentImage}
                    variant="square"
                    sx={{
                        width: '100%',
                        height: 150,
                        border: '2px solid #3b82f6',
                        borderRadius: 2
                    }}
                />
            )}

            {/* כפתורים */}
            <Stack direction="row" spacing={2} sx={{ width: fullWidth ? '100%' : 'auto' }}>
                <Button
                    component="label"
                    variant={variant}
                    startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    disabled={uploading || deleting}
                    fullWidth={fullWidth}
                    sx={{
                        bgcolor: variant === 'contained' ? '#3b82f6' : undefined,
                        '&:hover': { bgcolor: variant === 'contained' ? '#2563eb' : undefined }
                    }}
                >
                    {uploading ? 'מעלה...' : 'העלה תמונה'}
                    <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageUpload}
                    />
                </Button>

                {currentImage && !uploading && (
                    <IconButton
                        color="error"
                        onClick={handleImageDelete}
                        disabled={deleting}
                        sx={{
                            border: '1px solid',
                            borderColor: 'error.main'
                        }}
                    >
                        {deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                    </IconButton>
                )}
            </Stack>
        </Stack>
    );
};

export default ImageUploader;
